import PropTypes from 'prop-types';
import React from 'react';

import {
  getActivityDedupKey,
  observableToPromise,
} from '../../transport/directLineHelpers';
import WebChatFrameworkBase from '../../transport/webChatFrameworkBase';
import {
  isTypingActivity,
  mapDirectLineActivityToChatMessage,
  OPTION_TYPES,
} from '../../utils/directLineActivity';
import { SENDER_TYPES } from '../../utils/senderTypes';
import { shouldOpenSignInModal } from '../../utils/signInPrompt';
import ChatComposer from './ChatComposer';
import ChatMessageRegion from './ChatMessageRegion';

/**
 * Bridge between Web Chat's transport plumbing and the custom VA chat UI.
 * Exists as a separate component because Web Chat's hooks must be called
 * inside its `<Composer>` context; ChatConversation renders this inside one.
 * @component
 */
export default function ChatConversationComposerBridge({
  activeMessageIndex,
  disabled,
  errorMessage,
  isTyping,
  messageEndRef,
  messages,
  messageRegionRef,
  onActiveMessageIndexChange,
  onBotMessage,
  onComposerInputReady,
  onExitMessageNavigation,
  onFeedbackPending,
  onOpenSignInModal,
  onPostError,
  onRestoredUserMessage,
  onSendMessage,
  onStartNewChat,
  onTranscriptNavigationRequest,
  onTyping,
  showAiDisclaimer,
}) {
  // Composer hooks are the integration boundary: we reuse Web Chat transport/state
  // plumbing while rendering our own custom VA chat UI components.
  const activitiesHook = WebChatFrameworkBase.hooks?.useActivities;
  const postActivityHook = WebChatFrameworkBase.hooks?.usePostActivity;

  const [activities = []] = activitiesHook ? activitiesHook() : [[]];
  const postActivity = postActivityHook ? postActivityHook() : null;

  const seenActivityKeysRef = React.useRef(new Set());
  // User activities that arrive before this page sends anything are restored
  // history. Once the user sends, their messages are added by handleSubmit, and
  // the echoes of those sends must not be added a second time.
  const hasSentRef = React.useRef(false);

  React.useEffect(() => {
    activities.forEach(activity => {
      const dedupKey = getActivityDedupKey(activity);

      if (!dedupKey || seenActivityKeysRef.current.has(dedupKey)) {
        return;
      }

      seenActivityKeysRef.current.add(dedupKey);

      if (isTypingActivity(activity)) {
        onTyping();
        return;
      }

      const mapped = mapDirectLineActivityToChatMessage(activity);
      if (mapped?.sender === SENDER_TYPES.BOT) {
        onBotMessage(mapped);
      } else if (mapped?.sender === SENDER_TYPES.USER && !hasSentRef.current) {
        onRestoredUserMessage?.(mapped);
      }
    });
  }, [activities, onBotMessage, onRestoredUserMessage, onTyping]);

  const handleSubmit = async (messageText, optionType, sourceMessage) => {
    const trimmedText = messageText?.trim();
    if (!trimmedText || disabled) {
      return;
    }

    // An affirmative SignInPrompt option is a UI action: open the modal and
    // never echo or post the selection to the bot.
    if (shouldOpenSignInModal(sourceMessage, trimmedText)) {
      onOpenSignInModal?.();
      return;
    }

    const isFeedback = Boolean(sourceMessage?.isFeedback);
    const promptMessageId = isFeedback ? sourceMessage?.id : null;
    const feedbackTargetMessageId = isFeedback
      ? sourceMessage?.feedbackTargetMessageId
      : null;

    if (isFeedback && promptMessageId) {
      // Ignore further clicks while a feedback POST is in flight or already
      // acknowledged for this prompt.
      const targetMessage = messages.find(
        message => message.id === promptMessageId,
      );

      if (targetMessage?.feedbackPending || targetMessage?.feedbackSubmitted) {
        return;
      }
    }

    hasSentRef.current = true;

    if (optionType !== OPTION_TYPES.ICON) {
      onSendMessage(trimmedText);
    }

    if (!postActivity) {
      onPostError(isFeedback ? promptMessageId : null);
      return;
    }

    if (isFeedback && promptMessageId) {
      onFeedbackPending?.(promptMessageId);
    }

    try {
      const activity = {
        from: {
          id: 'user',
          role: SENDER_TYPES.USER,
        },
        text: trimmedText,
        type: 'message',
      };

      if (isFeedback) {
        activity.channelData = {
          isFeedback: true,
          ...(feedbackTargetMessageId ? { feedbackTargetMessageId } : {}),
        };
      }

      await observableToPromise(postActivity(activity));
    } catch (error) {
      // Defer to the parent's timer so a quick bot response can cancel the
      // failure and feedback-pending rollback before they surface.
      onPostError(isFeedback ? promptMessageId : null);
    }
  };

  return (
    <>
      <ChatMessageRegion
        activeMessageIndex={activeMessageIndex}
        errorMessage={errorMessage}
        isTyping={isTyping}
        messageEndRef={messageEndRef}
        messageRegionRef={messageRegionRef}
        messages={messages}
        onActiveMessageIndexChange={onActiveMessageIndexChange}
        onExitMessageNavigation={onExitMessageNavigation}
        onOptionSelect={handleSubmit}
        onStartNewChat={onStartNewChat}
        showAiDisclaimer={showAiDisclaimer}
      />
      <ChatComposer
        disabled={disabled}
        srStatusMessage={
          messages.some(message => message.feedbackPending)
            ? 'Feedback sent'
            : ''
        }
        onComposerInputReady={onComposerInputReady}
        onSubmit={handleSubmit}
        onTranscriptNavigationRequest={onTranscriptNavigationRequest}
      />
    </>
  );
}

ChatConversationComposerBridge.propTypes = {
  disabled: PropTypes.bool.isRequired,
  isTyping: PropTypes.bool.isRequired,
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      sender: PropTypes.oneOf([SENDER_TYPES.USER, SENDER_TYPES.BOT]).isRequired,
      text: PropTypes.string.isRequired,
      feedbackAcknowledgementTargetMessageId: PropTypes.string,
      feedbackPending: PropTypes.bool,
      feedbackSubmitted: PropTypes.bool,
      feedbackTargetMessageId: PropTypes.string,
      isFeedback: PropTypes.bool,
      optionType: PropTypes.oneOf(['icon', 'text']),
      options: PropTypes.arrayOf(PropTypes.string),
    }),
  ).isRequired,
  showAiDisclaimer: PropTypes.bool.isRequired,
  onBotMessage: PropTypes.func.isRequired,
  onPostError: PropTypes.func.isRequired,
  onSendMessage: PropTypes.func.isRequired,
  onTyping: PropTypes.func.isRequired,
  activeMessageIndex: PropTypes.number,
  errorMessage: PropTypes.string,
  messageEndRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }),
  messageRegionRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }),
  onActiveMessageIndexChange: PropTypes.func,
  onComposerInputReady: PropTypes.func,
  onExitMessageNavigation: PropTypes.func,
  onFeedbackPending: PropTypes.func,
  onOpenSignInModal: PropTypes.func,
  onRestoredUserMessage: PropTypes.func,
  onStartNewChat: PropTypes.func,
  onTranscriptNavigationRequest: PropTypes.func,
};
